import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Accessible searchable dropdown ("combobox") — typeahead + arrow-key
 * navigation + click-outside dismissal. Designed to be a drop-in replacement
 * for a native `<select>` where the option list is long.
 *
 * Props:
 * - value        — currently selected `value` (string)
 * - onChange     — callback (newValue, option?) → void
 * - options      — Array<{ value: string, label: string, hint?: string, meta?: any }>
 *                  OR Array<string> (converted internally)
 * - placeholder  — placeholder text when empty
 * - disabled
 * - allowFreeText — when true, users can type any value not in the list
 *                   (useful for capturing new master-data on the fly)
 * - clearable    — show ✕ to reset when a value is set
 * - testId       — root data-testid; the input receives `${testId}-input`
 * - noResultsText — override the "No matches" hint
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  allowFreeText = false,
  clearable = false,
  testId,
  noResultsText,
  className = "",
}) {
  const normalized = useMemo(() => (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o), [options]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() =>
    normalized.find((o) => o.value === value), [normalized, value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query) return normalized;
    const q = query.toLowerCase();
    return normalized.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.hint || "").toLowerCase().includes(q) ||
      (o.value || "").toLowerCase().includes(q));
  }, [query, normalized]);

  function commit(option) {
    onChange(option?.value ?? "", option);
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIdx]) {
        commit(filtered[activeIdx]);
      } else if (allowFreeText && query.trim()) {
        onChange(query.trim(), null);
        setQuery("");
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const displayValue = open ? query : (selected?.label ?? (allowFreeText ? (value || "") : ""));

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border bg-white ${disabled ? "opacity-60 cursor-not-allowed" : "border-[#e4e4e7] focus-within:border-[#002FA7]"}`}>
        <MagnifyingGlass size={13} className="text-[#a1a1aa] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); setOpen(true); }}
          onKeyDown={onKeyDown}
          className="flex-1 text-sm bg-transparent outline-none"
          data-testid={testId ? `${testId}-input` : undefined}
        />
        {clearable && value && !disabled && (
          <button type="button" onClick={(e) => { e.stopPropagation(); commit(null); }}
            className="text-[#a1a1aa] hover:text-[#FF2A2A]" tabIndex={-1}>
            <X size={12} weight="bold" />
          </button>
        )}
        <CaretDown size={12} weight="bold" className="text-[#a1a1aa]"
          onClick={() => !disabled && setOpen((v) => !v)} />
      </div>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-[#e4e4e7] bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="p-3 phizo-label text-xs">
              {noResultsText || (allowFreeText
                ? `No match — press Enter to keep '${query}'`
                : "No matches")}
            </div>
          ) : (
            filtered.map((o, i) => (
              <button
                type="button"
                key={o.value + i}
                onMouseDown={(e) => { e.preventDefault(); commit(o); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 ${i === activeIdx ? "bg-[#F0F4FF]" : "hover:bg-[#f4f4f5]"}`}
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
              >
                <span className="truncate">{o.label}</span>
                {o.hint && <span className="text-[10px] text-[#a1a1aa] mono shrink-0">{o.hint}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
